import { BitlyClient } from 'bitly'
import DOMPurify from 'dompurify'
import get from 'lodash/get'
import { ASSETS_URL } from '../constants/urls'
import { CUSTOM_HEADER_AND_FOOTER_CUSTOMERS } from '../constants'
import { upperFirst } from 'lodash'

const urlRewrite = urlBase => path => {
  const url = new URL(urlBase)
  const host = url.hostname
  const port = url.port ? `:${url.port}` : ''
  const protocol = url.protocol ? `${url.protocol}//` : ''
  const search = url.search ? url.search : ''
  const fullPath = `${protocol}${host}${port}/${path}${search}`
  return fullPath
}

export { slugify } from '@devicebits/slugify'

export const createBitlyLink = () => {
  const bitly = new BitlyClient('45aeb1f5d8a527c71d3635f5252e9b2d3ab17e4b')
  return bitly.shorten(window.location.href).then(
    function(response) {
      return response.data.url
    },
    function(error) {
      throw error
    }
  )
}

export const shadeColor = (color, percent) => {
  let R = parseInt(color.substring(1, 3), 16)
  let G = parseInt(color.substring(3, 5), 16)
  let B = parseInt(color.substring(5, 7), 16)

  R = parseInt((R * (100 + percent)) / 100, 10)
  G = parseInt((G * (100 + percent)) / 100, 10)
  B = parseInt((B * (100 + percent)) / 100, 10)

  R = R < 255 ? R : 255
  G = G < 255 ? G : 255
  B = B < 255 ? B : 255

  const RR = R.toString(16).length === 1 ? `0${R.toString(16)}` : R.toString(16)
  const GG = G.toString(16).length === 1 ? `0${G.toString(16)}` : G.toString(16)
  const BB = B.toString(16).length === 1 ? `0${B.toString(16)}` : B.toString(16)

  return `#${RR}${GG}${BB}`
}

export const hexToRgbA = (hex, alpha) => {
  let c
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('')
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]]
    }
    c = `0x${c.join('')}`
    return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(
      ','
    )},${alpha})`
  }
  throw new Error('Bad Hex')
}

export const trimLinkText = (text, length = 70) => {
  if (text && text.length > length) {
    text = text.substr(0, length)
    text = `${text.substr(0, Math.min(text.length, text.lastIndexOf(' ')))}...`
  }

  return text
}

export const stripHtmlTags = (text = '') => {
  const str = JSON.stringify(text)
  return str.replace(/<(?:.|\n)*?>/gm, '')
}

export const formatContentType = string => {
  const types = {
    tutorial: 'Tutorial',
    tutorials: 'Tutorial',
    guide: 'Guide',
    faq: 'FAQ',
    faqs: 'FAQ',
    pdf: 'PDF'
  }

  return types[string]
}

// TODO: move to essearch/helpers
export const getLanguageCol = (colName, langIndex = 1, longFormat = false) => {
  if (langIndex === 1 && longFormat === false) {
    return colName
  }
  return `${colName}_language_${langIndex}`
}

export const adddMessageListener = (type, callback) => {
  window.addEventListener('message', e => {
    if (e.data.type === type) {
      callback(e.data)
    }
  })
}

export const pipe = (...fns) => x => fns.reduce((v, f) => f(v), x)

export const sanitizeHTML = str => DOMPurify.sanitize(str)

export const trimString = (str = '', maxLength = 0) => {
  let text = ''
  const cleanedText = stripHtmlTags(str)
  const [firstSentence] = cleanedText.split('.')
  if (firstSentence.length >= 90 && firstSentence.length <= 120) {
    text = firstSentence
  } else {
    const trimmedString = cleanedText.substr(0, maxLength)
    text = trimmedString.substr(
      0,
      Math.min(trimmedString.length, trimmedString.lastIndexOf(' '))
    )
  }
  return text
}

export const iterate = () => {
  const accumulator = []
  return function next(obj, keyName) {
    Object.keys(obj).forEach(key => {
      if (key === keyName) {
        accumulator.push({ [key]: obj[key] })
      }
      if (typeof obj[key] === 'object' && obj[key]) {
        next(obj[key], keyName)
      }
    })
    return accumulator
  }
}

export const isCustomHeaderAndFooterCustomer = name =>
  CUSTOM_HEADER_AND_FOOTER_CUSTOMERS.includes(name)

export const loadExternal = (load, customer, isDataAvailable) =>
  load && isCustomHeaderAndFooterCustomer(customer) && isDataAvailable

export const createSteps = (arr, prop) => {
  if (!arr || !arr.length) {
    return []
  }
  return arr.map((item, index) => {
    /** the expected text format will be: "Select the \"Google\" folder." */
    const textFromObject = get(item, prop)
    /**
     * Call scriptHtmlTags to remove all HTML5 syntax-wrapping chars
     * Run replace function to remove \" and then again for remaining \
     * Essenntial as replaceAll is not widely available in all browsers and is only supported by node v15
     * @link https://stackoverflow.com/questions/13340131/string-prototype-replaceall-not-working
     */
    const text = stripHtmlTags(textFromObject)
      .replace(/(")/g, '')
      .replace(/\\/g, '')
    return `
    {
        "@type": "HowToStep",
        "name": "${index + 1}",
        "text": "${text}",
        "url": "${window.location}",
        "image": "${
          item.phoneScreenImage ? item.phoneScreenImage.imageLocation : ''
        }"
    }
`
  })
}

export const createStructuredData = (title, device, url, steps) => {
  return `
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "${title}",
      "description": "${title} ${device}",
      "image": {
          "@type": "ImageObject",
          "url": "${url}"
      },
      "totalTime": "PT2M",
      "supply": [
          {
              "@type": "HowToSupply",
              "name": "${device}"
          }
      ],
      "step": [ ${steps} ]
      }
  }
`
}

export const faqStructuredData = data => {
  return `
    {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [${data.map(item => {
          return `
            {
                "@type": "Question",
                "name": ${stripHtmlTags(item.question)},
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": ${stripHtmlTags(item.answer)}
                }
            }
        `
        })}]
    }
`
}

export const listStructuredData = (url, list) => {
  const urlBase = urlRewrite(url)
  return `
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "url": "${url}",
    "numberOfItems": ${list.length},
    "itemListElement": [${list.map((item, index) => {
      return `
        {
            "@type": "ListItem",
            "position": ${index + 1},
            "url": "${urlBase(item.slug)}",
            "name": ${JSON.stringify(item.text)}
      }
`
    })}]
  }`
}

export const faqsFlat = categories => {
  return categories
    .map(category =>
      category.children.map(subcat => subcat.children.map(faq => faq))
    )
    .flat(2)
}

export const faqsExtractData = data => {
  return data.map(item => {
    const { name, val, slug } = item
    return {
      question: name,
      answer: val,
      slug
    }
  })
}

const hostnames = {
  v2: 'https://www.straighttalk.com',
  v3: 'https://www.tracfone.com',
  v4: 'https://www.totalbyverizon.com'
}

const getAsset = url => fileName => `${url}/${fileName.replace(/^\//g, '')}` //remove first / if it is passed to avoid double slash

export const getHoverUrl = link => link.replace('black', 'green')

export const getIcon = setOfIcons =>
  getAsset(`${ASSETS_URL}/academy-assets/icons/${setOfIcons}`)

export const getUrl = (path, version) =>
  getAsset(hostnames[version] || 'https://sitfaem.tracfone.com/')(path)
//refers to S3 tpassets.devicebits.com/academy-assets/icons/{version}
const convertVersionToIconsFolder = version => {
  switch (version) {
    case 'v3':
      return 'tracfone'
    case 'v4':
      return 'tbv'

    default:
      return 'default'
  }
}
export const getVersionIcon = (icon, version = 'default') =>
  getIcon(convertVersionToIconsFolder(version))(icon)

/** Regex to validate link is a url */
const isUrl = new RegExp(
  '^(https?:\\/\\/)?' + // protocol
  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
  '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
  '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
)

/**
 * getAbsoluteLink
 * @description This function is used to get the correct url for the academy customers with versions
 * @see {@link src/components/Footer/FooterLinkList.js FooterLinkList}
 * @see {@link src/components/Footer/FooterLogo.js FooterLogo}
 * @param {string} link - linkUrl value from Footer api
 * @return {string} the correct url with customer domain or third party url
 */
export const getAbsoluteLink = (link, version) => {
  /** If `link` is a fully-formed url return it with a `www` subdomain if `sitfaem` happens to occur */
  if (isUrl.test(link)) {
    return link.replace('sitfaem', 'www')
  }

  /**
   * Otherwise, `link` is a relative path to an asset.
   * Call `getUrl` to prefix the `version`'s domain to `link` via `hostnames`
   * and return with `sitfaem` as `www` regardless.
   */
  return getUrl(link, version).replace('sitfaem', 'www')
}

export const upperCaseFirstLetter = str => upperFirst(str.toLowerCase())

/**
 * Get the css property value from a style object
 * to accommodate atypical highly-customized style overrides.
 *
 * This is a curried function on `key`: a version key ('v1', 'v2', 'v3')
 * Returns a function currying into `path`: a string object path
 * Returns a function currying into `lodash._get`
 * @example
  // import a style object and get the version key from context

  // The version based on the current customer ie: 'v2'
  const { versionKey } = useAppContext()

  // the parent styles object of the current version/customer
  const palette = getPalette(STYLES)(versionKey)

  // pass `palette` as `{...props}` to your styled-component
  text-transform: ${props => props.palette('tabs.textTransform.sm')}; // => 'capitalize'
 *
 * @param {object} parent The parent object to query.
 * @return {(key: string) => (path:  Array | string) => typeof get(parent[key], path)}
 */
export const getPalette = parent => key => path => get(parent[key], path)

/**
 * Generate svg icons from a src path
 * @param {string} src
 * @return {HTMLOrSVGImageElement}
 */
export const onSvgLoad = src => {
  document.querySelector(`#nav-${src} svg`)?.setAttribute('focusable', 'false')
}

/**
 * Replaces dummy script nodes with real script nodes: https://stackoverflow.com/questions/1197575/can-scripts-be-inserted-with-innerhtml
 * @param {HTMLOrSVGImageElement} node
 * @return {HTMLOrSVGImageElement}
 */

export const replaceScriptNodes = node => {
  // check if the current node is a script
  if (isScriptNode(node)) {
    // if it is, replace it with a clone of itself
    node.parentNode.replaceChild(cloneScriptNode(node), node)
  } else {
    // if not, iterate through its children and call this function recursively
    for (let child of node.childNodes) {
      replaceScriptNodes(child)
    }
  }

  return node
}

const cloneScriptNode = node => {
  // create a new script element
  const script = document.createElement('script')
  // set the text of the new element to the innerHTML of the original node
  script.text = node.innerHTML

  // copy the attributes of the original node to the new script element
  for (let attr of node.attributes) {
    script.setAttribute(attr.name, attr.value)
  }

  return script
}

const isScriptNode = node => {
  // check if the node's tagName is 'SCRIPT'
  return node.tagName === 'SCRIPT'
}



// WEBPACK FOOTER //
// ./src/helpers/utils.js